/**
 * QuickPropertiesPanel — Inline properties editor for selected sections.
 * Extracted from PageBuilder.js for modularity.
 */

import React from 'react';
import { ChevronDown, ChevronUp, Edit, Square } from 'lucide-react';
import FontPicker from '../FontPicker';
import { hasSection, getSettingsForComponent } from '../componentDefinitions';
import { getDynamicSection } from '../../utils/sectionRegistry';
import SectionPropertiesPanel from './SectionPropertiesPanel';
import { getComponentAtPath } from './utils/treeUtils';
import { SECTION_TYPES, getIconComponent } from './utils/builderHelpers';
import { ResponsiveDimensionControl, ResponsiveDirectionControl } from './ResponsiveControls';
import { usePageBuilder } from './context/PageBuilderContext';

export default function QuickPropertiesPanel() {
  const { doc, selection, responsive, page } = usePageBuilder();
  const section = selection.selectedSection;
  const onUpdate = doc.handleQuickUpdate;
  const onUpdateComponentProp = doc.updateComponentProp;
  const allSectionTypes = doc.allSectionTypes;
  const { selectedColumn } = selection;
  const activeBreakpoint = responsive.breakpoint;
  const onBreakpointChange = responsive.setBreakpoint;
  const onOpenAdvanced = (s) => selection.setEditingSection(s);

  // State for managing collapsible sections
  const [collapsedSections, setCollapsedSections] = React.useState({});

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  if (!section) {
    return (
      <div className="bg-gray-50 border-l border-gray-200 p-4 flex-shrink-0">
        <div className="text-center py-8 text-gray-500">
          <div className="mb-3">
            <Edit className="w-8 h-8 mx-auto text-gray-300" />
          </div>
          <h3 className="text-sm font-medium text-gray-400 mb-1">Select a section</h3>
          <p className="text-xs">Click on any section to edit its properties</p>
        </div>
      </div>
    );
  }

  const content = section.content;
  
  // Get section type info with multiple fallbacks
  let sectionTypeInfo;
  if (allSectionTypes && allSectionTypes[section.section_type]) {
    sectionTypeInfo = allSectionTypes[section.section_type];
  } else if (SECTION_TYPES && SECTION_TYPES[section.section_type]) {
    sectionTypeInfo = SECTION_TYPES[section.section_type];
  } else {
    sectionTypeInfo = { 
      label: section.section_type || 'Unknown Section', 
      icon: Square 
    };
  }

  const updateProperty = (key, value) => {
    if (key === 'actualColumns' && onUpdateComponentProp) {
      // Hot path: direct component prop update, bypassing actualColumns indirection
      const componentPath = section?.content?.selectedComponentPath;
      if (componentPath && Array.isArray(value) && value.length > 0) {
        onUpdateComponentProp(section.id, componentPath, value[0]);
        return;
      }
    }
    onUpdate(section.id, { [key]: value });
  };

  // Check if this is a dynamic section
  const isDynamicSection = sectionTypeInfo && sectionTypeInfo.isDynamic;

  // Helper function to render dynamic section settings
  const renderDynamicSectionSettings = () => {
    if (!isDynamicSection) return null;

    // Get the section's configuration from the registry
    const dynamicSection = getDynamicSection(section.section_type);
    if (!dynamicSection || !dynamicSection.settings) {
      console.warn('No settings found for dynamic section:', section.section_type);
      return null;
    }

    let sectionConfig = dynamicSection.settings;
    
    // Special handling for layout sections - they provide dynamic columnStructure
    // Check if this is a layout section by looking at the field type
    const hasLayoutField = sectionConfig.fields?.some(f => f.type === 'layout');
    if (hasLayoutField && content.selectedComponentPath) {
      // For layout sections, we need dynamic columnStructure based on selected component
      // We can't call renderFunction as it contains hooks, so we need to compute it here
      const components = Array.isArray(content?.components) ? content.components : [];
      const component = getComponentAtPath(components, content.selectedComponentPath);
      
      if (component) {
        // Build columnStructure dynamically based on component type
        const componentSettings = getSettingsForComponent(component);
        sectionConfig = {
          ...sectionConfig,
          columnStructure: {
            maxColumns: 100,
            fields: componentSettings
          }
        };
        
        // Extract actualColumns from the component's props to ensure we always have
        // the correct props for the selected component from the tree
        if (component.props) {
          content.actualColumns = [component.props];
        }
      }
    }

    const columns = content.actualColumns || [];
    
    // Find the count field (it will have a key ending with 'Count')
    const countField = sectionConfig.fields?.find(f => f.key && f.key.toLowerCase().includes('count'));

    return (
      <div className="space-y-4">
        {/* Column Count Control */}
        {countField && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              {countField.label || 'Number of Items'}
            </label>
            <select
              value={content[countField.key] || countField.default || 1}
              onChange={(e) => updateProperty(countField.key, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              {Array.from({ length: countField.max || sectionConfig.columnStructure?.maxColumns || 6 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>{num} {num === 1 ? countField.label?.replace(/Number of /, '').replace(/s$/, '') : countField.label?.replace(/Number of /, '')}</option>
              ))}
            </select>
          </div>
        )}

        {/* Column-specific settings */}
        {selectedColumn?.sectionId === section.id && selectedColumn?.columnIndex !== undefined && columns[selectedColumn.columnIndex] && sectionConfig.columnStructure && (
          <div className="space-y-3 border-t pt-4 border-b border-bottom-gray-200">
            <h4 className="text-xs font-semibold text-gray-700">
              {(() => {
                // For layout sections, show component type in title
                if (section.section_type === 'layout-1' && content.selectedComponentPath) {
                  const pathParts = content.selectedComponentPath.split('.');
                  if (pathParts.length === 1) {
                    return 'Row Settings';
                  } else if (pathParts.length === 3) {
                    return 'Column Settings';
                  } else {
                    // Determine component type from actualColumns
                    const componentProps = columns[selectedColumn.columnIndex];
                    // Try to infer type from props
                    if (componentProps.src !== undefined && componentProps.alt !== undefined) {
                      return 'Image Settings';
                    } else if (componentProps.text !== undefined && componentProps.url !== undefined) {
                      return 'Button Settings';
                    } else if (componentProps.content !== undefined && componentProps.element !== undefined) {
                      return 'Text Settings';
                    } else if (componentProps.icon !== undefined || componentProps.name !== undefined) {
                      return 'Icon Settings';
                    } else if (componentProps.height !== undefined && componentProps.width !== undefined && !componentProps.src) {
                      return 'Spacer Settings';
                    }
                    return 'Component Settings';
                  }
                }
                return `Item ${selectedColumn.columnIndex + 1} Settings`;
              })()}
            </h4>
            {(() => {
              // Group fields by section
              const fieldsBySection = {};
              sectionConfig.columnStructure.fields?.forEach(field => {
                const sectionName = field.section || 'Other';
                if (!fieldsBySection[sectionName]) {
                  fieldsBySection[sectionName] = [];
                }
                fieldsBySection[sectionName].push(field);
              });

              // Define section order
              const sectionOrder = ['Content', 'Typography', 'Layout & Dimensions', 'Style & Appearance', 'Behavior', 'Spacing', 'Other'];

              return sectionOrder.map((sectionName, sectionIndex) => {
                if (!fieldsBySection[sectionName] || fieldsBySection[sectionName].length === 0) {
                  return null;
                }

                // Check if this is the first visible section
                const isFirstSection = sectionIndex === 0 || sectionOrder.slice(0, sectionIndex).every(
                  name => !fieldsBySection[name] || fieldsBySection[name].length === 0
                );

                const sectionKey = `settings-section-${sectionName.replace(/\s+/g, '-').toLowerCase()}`;
                const isCollapsed = collapsedSections[sectionKey];

                return (
                  <div key={sectionName} className={`${isFirstSection ? '' : 'border-t border-gray-200 pt-3'}`}>
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      className="w-full flex items-center justify-between mb-3 hover:text-gray-800 transition-colors"
                    >
                      <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{sectionName}</h5>
                      {isCollapsed ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-3">
                        {fieldsBySection[sectionName].map(field => (
                          <div key={field.key}>
            {field.type === 'text' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <input
                      type="text"
                      value={columns[selectedColumn.columnIndex][field.key] || ''}
                      onChange={(e) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: e.target.value
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      placeholder={field.default}
                    />
                  </div>
                )}
                {field.type === 'textarea' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <textarea
                      value={columns[selectedColumn.columnIndex][field.key] || ''}
                      onChange={(e) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: e.target.value
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      rows="3"
                      placeholder={field.default}
                    />
                  </div>
                )}
                {field.type === 'color' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={columns[selectedColumn.columnIndex][field.key] || field.default || '#ffffff'}
                        onChange={(e) => {
                          const updatedColumns = [...columns];
                          updatedColumns[selectedColumn.columnIndex] = {
                            ...updatedColumns[selectedColumn.columnIndex],
                            [field.key]: e.target.value
                          };
                          updateProperty('actualColumns', updatedColumns);
                        }}
                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={columns[selectedColumn.columnIndex][field.key] || field.default || '#ffffff'}
                        onChange={(e) => {
                          const updatedColumns = [...columns];
                          updatedColumns[selectedColumn.columnIndex] = {
                            ...updatedColumns[selectedColumn.columnIndex],
                            [field.key]: e.target.value
                          };
                          updateProperty('actualColumns', updatedColumns);
                        }}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                        placeholder={field.default}
                      />
                    </div>
                  </div>
                )}
                {field.type === 'number' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <input
                      type="number"
                      value={columns[selectedColumn.columnIndex][field.key] ?? field.default ?? ''}
                      onChange={(e) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: parseFloat(e.target.value)
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                  </div>
                )}
                {field.type === 'select' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <select
                      value={columns[selectedColumn.columnIndex][field.key] || field.default || ''}
                      onChange={(e) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: e.target.value
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    >
                      {field.options?.map(option => (
                        <option key={typeof option === 'string' ? option : option.value} value={typeof option === 'string' ? option : option.value}>
                          {typeof option === 'string' ? option : option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {field.type === 'fontFamily' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <FontPicker
                      value={columns[selectedColumn.columnIndex][field.key] || field.default || 'System Default'}
                      onChange={(fontFamily) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: fontFamily
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      className="text-xs"
                    />
                  </div>
                )}
                {field.type === 'paddingFull' && (
                  <div className="border border-gray-200 rounded">
                    <button
                      onClick={() => toggleSection(`spacing-padding-${field.key}`)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <label className="text-xs font-medium text-gray-700 cursor-pointer">{field.label}</label>
                      {collapsedSections[`spacing-padding-${field.key}`] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {!collapsedSections[`spacing-padding-${field.key}`] && (
                      <div className="p-3">
                        {(() => {
                      // Get current padding value - could be object or number for backward compatibility
                      const currentPadding = columns[selectedColumn.columnIndex][field.key];
                      let paddingObj;
                      
                      if (typeof currentPadding === 'object' && currentPadding !== null) {
                        paddingObj = {
                          top: currentPadding.top ?? 0,
                          bottom: currentPadding.bottom ?? 0,
                          left: currentPadding.left ?? 0,
                          right: currentPadding.right ?? 0,
                          unit: currentPadding.unit || 'px'
                        };
                      } else if (typeof currentPadding === 'number') {
                        // Backward compatibility: convert number to object
                        paddingObj = {
                          top: currentPadding,
                          bottom: currentPadding,
                          left: currentPadding,
                          right: currentPadding,
                          unit: 'px'
                        };
                      } else {
                        // Use default
                        paddingObj = field.default || { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' };
                      }
                      
                      const updatePadding = (side, value) => {
                        const updatedColumns = [...columns];
                        const parsed = parseFloat(value);
                        const newPadding = { ...paddingObj, [side]: isNaN(parsed) ? 0 : parsed };
                        
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: newPadding
                        };
                        
                        updateProperty('actualColumns', updatedColumns);
                      };
                      
                      return (
                        <div className="space-y-2">
                          {/* Unit selector */}
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Unit</label>
                            <select
                              value={paddingObj.unit}
                              onChange={(e) => {
                                const updatedColumns = [...columns];
                                const oldUnit = paddingObj.unit;
                                const newUnit = e.target.value;
                                
                                // Convert values to new unit
                                const convertValue = (value, fromUnit, toUnit) => {
                                  if (value === 0 || value === '0') return 0;
                                  
                                  const numValue = parseFloat(value);
                                  if (isNaN(numValue)) return 0;
                                  
                                  // Conversion map: everything to px first, then to target unit
                                  const toPx = {
                                    'px': (v) => v,
                                    'rem': (v) => v * 16,  // 1rem = 16px
                                    'em': (v) => v * 16,   // 1em = 16px (approximate)
                                    'vh': (v) => v * (window.innerHeight / 100),
                                    'vw': (v) => v * (window.innerWidth / 100),
                                    '%': (v) => v  // Keep % as-is since context-dependent
                                  };
                                  
                                  const fromPx = {
                                    'px': (v) => v,
                                    'rem': (v) => v / 16,
                                    'em': (v) => v / 16,
                                    'vh': (v) => v / (window.innerHeight / 100),
                                    'vw': (v) => v / (window.innerWidth / 100),
                                    '%': (v) => v  // Keep % as-is
                                  };
                                  
                                  // Convert to px first, then to target unit
                                  const pxValue = toPx[fromUnit] ? toPx[fromUnit](numValue) : numValue;
                                  const convertedValue = fromPx[toUnit] ? fromPx[toUnit](pxValue) : pxValue;
                                  
                                  // Round to 2 decimal places
                                  return Math.round(convertedValue * 100) / 100;
                                };
                                
                                const newPadding = {
                                  top: convertValue(paddingObj.top, oldUnit, newUnit),
                                  bottom: convertValue(paddingObj.bottom, oldUnit, newUnit),
                                  left: convertValue(paddingObj.left, oldUnit, newUnit),
                                  right: convertValue(paddingObj.right, oldUnit, newUnit),
                                  unit: newUnit
                                };
                                
                                updatedColumns[selectedColumn.columnIndex] = {
                                  ...updatedColumns[selectedColumn.columnIndex],
                                  [field.key]: newPadding
                                };
                                updateProperty('actualColumns', updatedColumns);
                              }}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            >
                              <option value="px">px</option>
                              <option value="rem">rem</option>
                              <option value="em">em</option>
                              <option value="vh">vh</option>
                              <option value="vw">vw</option>
                              <option value="%">%</option>
                            </select>
                          </div>
                          
                          {/* Padding inputs in a 2x2 grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Top</label>
                              <input
                                name={`padding-top-${field.key}-${selectedColumn.columnIndex}`}
                                type="number"
                                value={paddingObj.top}
                                onChange={(e) => updatePadding('top', e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                min="0"
                                step="0.1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Bottom</label>
                              <input
                                name={`padding-bottom-${field.key}-${selectedColumn.columnIndex}`}
                                type="number"
                                value={paddingObj.bottom}
                                onChange={(e) => updatePadding('bottom', e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                min="0"
                                step="0.1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Left</label>
                              <input
                                name={`padding-left-${field.key}-${selectedColumn.columnIndex}`}
                                type="number"
                                value={paddingObj.left}
                                onChange={(e) => updatePadding('left', e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                min="0"
                                step="0.1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Right</label>
                              <input
                                name={`padding-right-${field.key}-${selectedColumn.columnIndex}`}
                                type="number"
                                value={paddingObj.right}
                                onChange={(e) => updatePadding('right', e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                min="0"
                                step="0.1"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                      </div>
                    )}
                  </div>
                )}
                {field.type === 'marginFull' && (
                  <div className="border border-gray-200 rounded">
                    <button
                      onClick={() => toggleSection(`spacing-margin-${field.key}`)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <label className="text-xs font-medium text-gray-700 cursor-pointer">{field.label}</label>
                      {collapsedSections[`spacing-margin-${field.key}`] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {!collapsedSections[`spacing-margin-${field.key}`] && (
                      <div className="p-3">
                        {(() => {
                      // Get current margin value - could be object or number for backward compatibility
                      const currentMargin = columns[selectedColumn.columnIndex][field.key];
                      let marginObj;
                      
                      if (typeof currentMargin === 'object' && currentMargin !== null) {
                        marginObj = {
                          top: currentMargin.top ?? 0,
                          bottom: currentMargin.bottom ?? 0,
                          left: currentMargin.left ?? 0,
                          right: currentMargin.right ?? 0,
                          unit: currentMargin.unit || 'px'
                        };
                      } else if (typeof currentMargin === 'number') {
                        // Backward compatibility: convert number to object
                        marginObj = {
                          top: currentMargin,
                          bottom: currentMargin,
                          left: currentMargin,
                          right: currentMargin,
                          unit: 'px'
                        };
                      } else {
                        // Use default
                        marginObj = field.default || { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' };
                      }
                      
                      const updateMargin = (side, value) => {
                        const updatedColumns = [...columns];
                        // Handle 'auto' or convert to number
                        let processedValue;
                        const trimmedValue = String(value).trim().toLowerCase();
                        if (trimmedValue === 'auto') {
                          processedValue = 'auto';
                        } else {
                          const parsed = parseFloat(value);
                          processedValue = isNaN(parsed) ? 0 : parsed;
                        }
                        
                        const newMargin = { ...marginObj, [side]: processedValue };
                        
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: newMargin
                        };
                        
                        updateProperty('actualColumns', updatedColumns);
                      };
                      
                      return (
                        <div className="space-y-2">
                          {/* Unit selector */}
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Unit</label>
                            <select
                              value={marginObj.unit}
                              onChange={(e) => {
                                const updatedColumns = [...columns];
                                const oldUnit = marginObj.unit;
                                const newUnit = e.target.value;
                                
                                // Convert values to new unit (skip 'auto' values)
                                const convertValue = (value, fromUnit, toUnit) => {
                                  // Don't convert 'auto'
                                  if (value === 'auto') return value;
                                  if (value === 0 || value === '0') return 0;
                                  
                                  const numValue = parseFloat(value);
                                  if (isNaN(numValue)) return 0;
                                  
                                  // Conversion map: everything to px first, then to target unit
                                  const toPx = {
                                    'px': (v) => v,
                                    'rem': (v) => v * 16,  // 1rem = 16px
                                    'em': (v) => v * 16,   // 1em = 16px (approximate)
                                    'vh': (v) => v * (window.innerHeight / 100),
                                    'vw': (v) => v * (window.innerWidth / 100),
                                    '%': (v) => v  // Keep % as-is since context-dependent
                                  };
                                  
                                  const fromPx = {
                                    'px': (v) => v,
                                    'rem': (v) => v / 16,
                                    'em': (v) => v / 16,
                                    'vh': (v) => v / (window.innerHeight / 100),
                                    'vw': (v) => v / (window.innerWidth / 100),
                                    '%': (v) => v  // Keep % as-is
                                  };
                                  
                                  // Convert to px first, then to target unit
                                  const pxValue = toPx[fromUnit] ? toPx[fromUnit](numValue) : numValue;
                                  const convertedValue = fromPx[toUnit] ? fromPx[toUnit](pxValue) : pxValue;
                                  
                                  // Round to 2 decimal places
                                  return Math.round(convertedValue * 100) / 100;
                                };
                                
                                const newMargin = {
                                  top: convertValue(marginObj.top, oldUnit, newUnit),
                                  bottom: convertValue(marginObj.bottom, oldUnit, newUnit),
                                  left: convertValue(marginObj.left, oldUnit, newUnit),
                                  right: convertValue(marginObj.right, oldUnit, newUnit),
                                  unit: newUnit
                                };
                                
                                updatedColumns[selectedColumn.columnIndex] = {
                                  ...updatedColumns[selectedColumn.columnIndex],
                                  [field.key]: newMargin
                                };
                                updateProperty('actualColumns', updatedColumns);
                              }}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            >
                              <option value="px">px</option>
                              <option value="rem">rem</option>
                              <option value="em">em</option>
                              <option value="vh">vh</option>
                              <option value="vw">vw</option>
                              <option value="%">%</option>
                            </select>
                          </div>
                          
                          {/* Margin inputs in a 2x2 grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Top</label>
                              <input
                                name={`margin-top-${field.key}-${selectedColumn.columnIndex}`}
                                type="number"
                                value={marginObj.top}
                                onChange={(e) => updateMargin('top', e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                placeholder="0"
                                step="0.1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Bottom</label>
                              <input
                                name={`margin-bottom-${field.key}-${selectedColumn.columnIndex}`}
                                type="number"
                                value={marginObj.bottom}
                                onChange={(e) => updateMargin('bottom', e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                placeholder="0"
                                step="0.1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Left</label>
                              <input
                                name={`margin-left-${field.key}-${selectedColumn.columnIndex}`}
                                type={marginObj.left === 'auto' ? 'text' : 'number'}
                                value={marginObj.left === 'auto' ? 'auto' : marginObj.left}
                                onChange={(e) => updateMargin('left', e.target.value)}
                                disabled={marginObj.left === 'auto'}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-600"
                                placeholder="0"
                                step="0.1"
                              />
                              <button
                                type="button"
                                onClick={() => updateMargin('left', marginObj.left === 'auto' ? '0' : 'auto')}
                                className={`w-full mt-1 px-2 py-1 text-xs rounded border transition-colors ${
                                  marginObj.left === 'auto'
                                    ? 'bg-indigo-500 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                                title={marginObj.left === 'auto' ? 'Set to 0' : 'Set to auto'}
                              >
                                {marginObj.left === 'auto' ? '✓ Auto' : 'Set Auto'}
                              </button>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Right</label>
                              <input
                                name={`margin-right-${field.key}-${selectedColumn.columnIndex}`}
                                type={marginObj.right === 'auto' ? 'text' : 'number'}
                                value={marginObj.right === 'auto' ? 'auto' : marginObj.right}
                                onChange={(e) => updateMargin('right', e.target.value)}
                                disabled={marginObj.right === 'auto'}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-600"
                                placeholder="0"
                                step="0.1"
                              />
                              <button
                                type="button"
                                onClick={() => updateMargin('right', marginObj.right === 'auto' ? '0' : 'auto')}
                                className={`w-full mt-1 px-2 py-1 text-xs rounded border transition-colors ${
                                  marginObj.right === 'auto'
                                    ? 'bg-indigo-500 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                                title={marginObj.right === 'auto' ? 'Set to 0' : 'Set to auto'}
                              >
                                {marginObj.right === 'auto' ? '✓ Auto' : 'Set Auto'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                      </div>
                    )}
                  </div>
                )}
                {field.type === 'dimensionWithUnit' && (
                  <div className="border border-gray-200 rounded">
                    <button
                      onClick={() => toggleSection(`dimension-${field.key}`)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <label className="text-xs font-medium text-gray-700 cursor-pointer">{field.label}</label>
                      {collapsedSections[`dimension-${field.key}`] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {!collapsedSections[`dimension-${field.key}`] && (
                      <div className="p-3">
                        {(() => {
                          // Get current dimension value
                          const currentDimension = columns[selectedColumn.columnIndex][field.key];
                          let dimensionObj;

                          if (typeof currentDimension === 'object' && currentDimension !== null) {
                            dimensionObj = {
                              value: currentDimension.value ?? 'auto',
                              unit: currentDimension.unit || 'px'
                            };
                          } else {
                            // Use default
                            dimensionObj = field.default || { value: 'auto', unit: 'px' };
                          }

                          const isAuto = dimensionObj.value === 'auto';

                          const updateDimension = (updates) => {
                            const updatedColumns = [...columns];
                            const newDimension = { ...dimensionObj, ...updates };

                            updatedColumns[selectedColumn.columnIndex] = {
                              ...updatedColumns[selectedColumn.columnIndex],
                              [field.key]: newDimension
                            };

                            updateProperty('actualColumns', updatedColumns);
                          };

                          const toggleAuto = () => {
                            updateDimension({ value: isAuto ? 0 : 'auto' });
                          };

                          return (
                            <div className="space-y-2">
                              {/* Auto checkbox */}
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isAuto}
                                  onChange={toggleAuto}
                                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="ml-2 text-xs text-gray-700">Auto (no constraint)</span>
                              </label>

                              {/* Value input - disabled when auto */}
                              {!isAuto && (
                                <>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Value</label>
                                    <input
                                      type="number"
                                      value={dimensionObj.value}
                                      onChange={(e) => updateDimension({ value: parseFloat(e.target.value) || 0 })}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                      placeholder="0"
                                      step="1"
                                      min="0"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Unit</label>
                                    <select
                                      value={dimensionObj.unit}
                                      onChange={(e) => updateDimension({ unit: e.target.value })}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                    >
                                      <option value="px">px - Pixels</option>
                                      <option value="%">% - Percentage</option>
                                      <option value="rem">rem - Root EM</option>
                                      <option value="em">em - EM</option>
                                      <option value="vh">vh - Viewport Height</option>
                                      <option value="vw">vw - Viewport Width</option>
                                    </select>
                                  </div>

                                  {/* Preview/hint */}
                                  <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                                    Preview: {dimensionObj.value}{dimensionObj.unit}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Responsive Dimension Control (width/maxWidth with breakpoints) */}
                {field.type === 'responsiveDimension' && (
                  <div className="border border-gray-200 rounded">
                    <button
                      onClick={() => toggleSection(`responsive-dimension-${field.key}`)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <label className="text-xs font-medium text-gray-700 cursor-pointer">{field.label}</label>
                      {collapsedSections[`responsive-dimension-${field.key}`] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {!collapsedSections[`responsive-dimension-${field.key}`] && (
                      <div className="p-3">
                        <ResponsiveDimensionControl
                          field={field}
                          currentValue={columns[selectedColumn.columnIndex][field.key]}
                          columns={columns}
                          selectedColumn={selectedColumn}
                          updateProperty={updateProperty}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Responsive Direction Control (direction with breakpoints) */}
                {field.type === 'responsiveDirection' && (
                  <div className="border border-gray-200 rounded">
                    <button
                      onClick={() => toggleSection(`responsive-direction-${field.key}`)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <label className="text-xs font-medium text-gray-700 cursor-pointer">{field.label}</label>
                      {collapsedSections[`responsive-direction-${field.key}`] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    {!collapsedSections[`responsive-direction-${field.key}`] && (
                      <div className="p-3">
                        <ResponsiveDirectionControl
                          field={field}
                          currentValue={columns[selectedColumn.columnIndex][field.key]}
                          columns={columns}
                          selectedColumn={selectedColumn}
                          updateProperty={updateProperty}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border-l border-gray-200 flex flex-col flex-shrink-0 h-full">
      {/* Section Header */}
      <div className="flex items-center justify-between p-3 pb-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          {sectionTypeInfo?.icon && React.createElement(
            typeof sectionTypeInfo.icon === 'string' ? getIconComponent(sectionTypeInfo.icon) : sectionTypeInfo.icon, 
            { className: "w-3 h-3 text-gray-500" }
          )}
          <h3 className="text-sm font-medium text-gray-900">{sectionTypeInfo?.label}</h3>
        </div>
        {/* Only show Advanced button for non-dynamic, non-builtin sections */}
        {!isDynamicSection && !hasSection(section.section_type) && (
          <button
            onClick={() => onOpenAdvanced(section)}
            className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded px-1.5 py-0.5"
          >
            Advanced
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        <div className="space-y-4">
        {/* Built-in Sections - use SectionPropertiesPanel */}
        {hasSection(section.section_type) ? (
          <SectionPropertiesPanel
            sectionType={section.section_type}
            content={content}
            onUpdate={updateProperty}
            showHeader={false}
            page={page}
            activeBreakpoint={activeBreakpoint}
            onBreakpointChange={onBreakpointChange}
          />
        ) : isDynamicSection ? (
          /* Dynamic Section Settings */
          renderDynamicSectionSettings()
        ) : (
          /* Unknown section type - show basic controls */
          <div className="text-center py-4 text-gray-500">
            <p className="text-xs">No settings available for this section type</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
